import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail, Phone, MapPin, Clock, User, Send, ExternalLink,
  MessageCircle, Ticket, BookOpen, Megaphone, ChevronRight, CheckCircle2,
  Zap, Headphones, ShieldCheck,
} from 'lucide-react';
import { API_BASE_URL } from '../apiConfig';

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';

/* Resize the hero image with the values below. On desktop it's an ABSOLUTE overlay,
   so making it bigger only overlaps — the text and everything below never move.
   (If you enlarge it a lot and it clips, bump the `lg:min-h-[380px]` on its wrapper div.) */
const HERO_IMAGE = {
  desktopWidth: '750px',   // ← increase this to make the image bigger on desktop
  desktopHeight: 'auto',   // ← 'auto' keeps the ratio, or set e.g. '360px'
  offsetX: '0px',          // ← horizontal nudge (negative = push right / off edge, positive = pull left)
  offsetY: '0px',          // ← vertical nudge, e.g. '-20px'
};

const API_URL = `${API_BASE_URL}/contact`;
const MAPS_URL = 'https://www.google.com/maps/search/?api=1&query=SmartEdz+Solutions+Madhapur+Hyderabad';

const INFO = [
  { Icon: Mail, title: 'Email Us', lines: ['support@smartedz.com', 'info@smartedz.com'] },
  { Icon: Phone, title: 'Call Us', lines: ['+91 6399191691'] },
  { Icon: MapPin, title: 'Visit Us', lines: ['SmartEdz LLP', 'Jubilee Hills,', 'Hyderabad,', 'Telangana - 500045, India'] },
  { Icon: Clock, title: 'Working Hours', lines: ['Mon - Sat: 9:00 AM - 6:00 PM', 'Sunday: Closed'] },
];

// Added `path` to each object to define where the link should navigate
const WAYS = [
  { Icon: MessageCircle, color: '#16a34a', title: 'Live Chat', desc: 'Chat with our support team in real-time.', cta: 'Start Chat', path: '/livechat' },
  { Icon: Ticket, color: '#9333ea', title: 'Support Ticket', desc: "Raise a ticket and we'll get back to you.", cta: 'Create Ticket', path: '/supportticket' },
  { Icon: BookOpen, color: '#3284c7', title: 'Help Center', desc: 'Find answers to common questions and guides.', cta: 'Visit Help Center', path: '/helpcenter' },
  { Icon: Megaphone, color: '#f29132', title: 'Feedback', desc: 'Share your feedback to help us improve.', cta: 'Give Feedback', path: '/feedback' },
];

const SUBJECTS = ['General Inquiry', 'Sales Inquiry', 'Technical Support', 'Partnership', 'Feedback', 'Other'];

const HERO_FEATURES = [
  { Icon: Zap, top: 'Quick', bottom: 'Response' },
  { Icon: Headphones, top: 'Expert', bottom: 'Support' },
  { Icon: MessageCircle, top: 'Multiple', bottom: 'Channels' },
  { Icon: ShieldCheck, top: '100%', bottom: 'Secure' },
];

const FIELD = 'w-full h-11 rounded-lg ring-1 ring-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-primary transition';

const ContactUs = () => {
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!form.fullName || !form.email || !form.subject || !form.message) {
      setStatus('error'); setMessage('Please fill in all required fields (*).'); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setStatus('error'); setMessage('Please enter a valid email address.'); return;
    }
    try {
      setStatus('loading');
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Request failed');
      setStatus('success');
      setMessage(data.message || "Message sent! We'll get back to you soon.");
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="bg-white min-h-screen overflow-x-hidden">
      <style>{`
        /* Hero image: absolute overlay on desktop so resizing it never shifts the layout */
        @media (min-width: 1024px) {
          .se-hero-img {
            position: absolute;
            top: calc(50% + var(--hero-y, 0px));
            right: var(--hero-x, 0px);
            transform: translateY(-50%);
            width: var(--hero-w, 440px);
            height: var(--hero-h, auto);
            max-width: none;
            margin: 0;
          }
        }
      `}</style>

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#eef4fb] via-[#f5f9fd] to-white">
        <div className={`${CONTAINER} relative py-10 lg:py-12`}>
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <nav className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                <Link to="/" className="hover:text-primary">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">Contact Us</span>
              </nav>
              <span className="block text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">Get in Touch</span>
              <h1 className="text-4xl sm:text-5xl xl:text-6xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>Contact Us</h1>
              <p className="mt-5 text-lg text-slate-500 max-w-lg leading-relaxed">
                Let's build something extraordinary together. Share your requirements with us, and our experts will connect with you to discuss the best solution for your business.
              </p>

              <div className="grid grid-cols-4 gap-3 mt-8 max-w-lg">
                {HERO_FEATURES.map((f) => (
                  <div key={f.bottom} className="flex flex-col items-center text-center">
                    <div className="w-12 h-12 rounded-full bg-white ring-1 ring-slate-200 shadow-sm flex items-center justify-center mb-2">
                      <f.Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="font-bold text-slate-800 text-xs leading-tight">{f.top}</div>
                    <div className="text-[11px] text-slate-500 leading-tight">{f.bottom}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden sm:block relative lg:min-h-[380px]">
              <img
                src="/Contact_Img.png"
                alt="Contact SmartEdz"
                className="se-hero-img w-full h-auto max-w-[440px] mx-auto"
                draggable="false"
                style={{
                  '--hero-w': HERO_IMAGE.desktopWidth,
                  '--hero-h': HERO_IMAGE.desktopHeight,
                  '--hero-x': HERO_IMAGE.offsetX,
                  '--hero-y': HERO_IMAGE.offsetY,
                }}
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================= FORM + INFO ================= */}
      <section className={`${CONTAINER} py-10`}>
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          {/* form */}
          <div className="lg:col-span-2 rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-6 sm:p-8">
            <h2 className="text-xl font-extrabold mb-6" style={{ color: '#13294b' }}>Send Us a Message</h2>

            {status === 'success' ? (
              <div className="rounded-xl bg-green-50 ring-1 ring-green-200 p-6 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-3">
                  <CheckCircle2 className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="font-bold text-slate-800 text-lg mb-1">Message Sent!</h3>
                <p className="text-slate-600 text-sm">{message}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input value={form.fullName} onChange={set('fullName')} placeholder="Enter your full name" className={`${FIELD} pl-9`} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input type="email" value={form.email} onChange={set('email')} placeholder="Enter your email" className={`${FIELD} pl-9`} />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number</label>
                  <div className="relative">
                    <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input value={form.phone} onChange={set('phone')} placeholder="Enter your phone number" className={`${FIELD} pl-9`} />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Subject <span className="text-red-500">*</span></label>
                  <select value={form.subject} onChange={set('subject')} className={`${FIELD} appearance-none`}>
                    <option value="">Select a subject</option>
                    {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Message <span className="text-red-500">*</span></label>
                  <textarea value={form.message} onChange={set('message')} rows={5} maxLength={1000} placeholder="Type your message here..."
                    className="w-full rounded-lg ring-1 ring-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-primary transition resize-y" />
                  <div className="text-right text-xs text-slate-400 mt-1">{form.message.length}/1000</div>
                </div>

                {status === 'error' && message && (
                  <div className="rounded-lg bg-red-50 ring-1 ring-red-200 text-red-600 text-sm px-4 py-2.5">{message}</div>
                )}

                <button type="submit" disabled={status === 'loading'}
                  className="inline-flex items-center gap-2 rounded-lg px-6 py-3 font-semibold text-white bg-primary shadow-lg shadow-primary/30 hover:brightness-110 transition disabled:opacity-60">
                  {status === 'loading' ? 'Sending...' : 'Send Message'}
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>

          {/* right column */}
          <div className="space-y-6">
            <div className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-6">
              <h2 className="text-xl font-extrabold mb-1" style={{ color: '#13294b' }}>Get in Touch</h2>
              <p className="text-sm text-slate-500 mb-5">We're here to help you with any questions or support you need.</p>
              <div className="space-y-5">
                {INFO.map((it) => (
                  <div key={it.title} className="flex gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <it.Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-bold text-slate-800 text-sm">{it.title}</div>
                      {it.lines.map((l) => <div key={l} className="text-sm text-slate-500 leading-snug">{l}</div>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-white ring-1 ring-slate-200 shadow-sm p-6">
              <h2 className="text-lg font-extrabold mb-4" style={{ color: '#13294b' }}>Our Location</h2>
              <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 mb-4">
                <img src="/Contact_Map.png" alt="SmartEdz location" className="w-full h-auto block"
                  onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
              </div>
              <a href={MAPS_URL} target="_blank" rel="noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-primary bg-primary/5 ring-1 ring-primary/20 hover:bg-primary/10 transition">
                <ExternalLink className="w-4 h-4" /> Open in Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ================= OTHER WAYS ================= */}
      <section className={`${CONTAINER} pb-16`}>
        <div className="rounded-2xl bg-[#f5f9fd] ring-1 ring-slate-200 p-6 sm:p-8">
          <h2 className="text-2xl font-extrabold mb-8" style={{ color: '#13294b' }}>Other Ways to Reach Us</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WAYS.map((w) => (
              <div key={w.title} className="bg-white rounded-2xl ring-1 ring-slate-100 shadow-sm p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${w.color}1A` }}>
                  <w.Icon className="w-7 h-7" style={{ color: w.color }} />
                </div>
                <h3 className="font-bold text-slate-800 mb-1.5">{w.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed mb-4">{w.desc}</p>
                
                {/* Changed from <button> to <Link> and added hover:opacity-80 */}
                <Link 
                  to={w.path}
                  className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold w-full transition hover:opacity-80"
                  style={{ color: w.color, backgroundColor: `${w.color}12` }}
                >
                  {w.cta}
                </Link>
                
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;